import { useState, useCallback, useRef, useEffect, useMemo, MouseEvent } from 'react';
import { useParams, useSearchParams, useLocation, useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient, useMutation } from '@tanstack/react-query';
import {
  Folder,
  FolderPlus,
  File,
  FileText,
  FileImage,
  FileVideo,
  FileAudio,
  FileSpreadsheet,
  FileArchive,
  Presentation,
  Upload,
  Download,
  ChevronRight,
  ChevronDown,
  Loader2,
  Grid,
  List,
  CheckCircle2,
  AlertCircle,
  Trash2,
  MoreVertical,
  Edit2,
  Search,
  X,
  MessageSquare,
  CheckSquare,
  RotateCcw,
} from 'lucide-react';
import { attachmentsApi, projectsApi, ProjectFile, ProjectFilesQuery, FileFolder } from '@/lib/api';
import { useAuthStore } from '@/stores/auth.store';
import { useProjectRoles } from '@/hooks/useProjectRoles';
import { Button } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';
import { FilePreviewModal } from '@/components/ui/FilePreviewModal';
import { AttachmentThumbnail } from '@/components/ui/AttachmentThumbnail';
import { CreateFolderModal } from '@/components/modals/CreateFolderModal';
import { formatBytes, cn } from '@/lib/utils';

// MIME category icons
function getMimeCategoryIcon(mimeCategory?: string, mimeType?: string) {
  const category = mimeCategory || getMimeCategoryFromType(mimeType || '');

  switch (category) {
    case 'image':
      return FileImage;
    case 'video':
      return FileVideo;
    case 'audio':
      return FileAudio;
    case 'document':
      return FileText;
    case 'spreadsheet':
      return FileSpreadsheet;
    case 'presentation':
      return Presentation;
    case 'archive':
      return FileArchive;
    default:
      return File;
  }
}

function getMimeCategoryFromType(mimeType: string): string {
  const type = mimeType.toLowerCase();
  if (type.startsWith('image/')) return 'image';
  if (type.startsWith('video/')) return 'video';
  if (type.startsWith('audio/')) return 'audio';
  if (type.includes('pdf') || type.includes('word') || type.includes('document') || type.includes('text/')) return 'document';
  if (type.includes('excel') || type.includes('spreadsheet') || type.includes('csv')) return 'spreadsheet';
  if (type.includes('powerpoint') || type.includes('presentation')) return 'presentation';
  if (type.includes('zip') || type.includes('rar') || type.includes('tar') || type.includes('archive')) return 'archive';
  return 'other';
}

function formatDate(dateString?: string) {
  if (!dateString) return '';
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: date.getFullYear() !== new Date().getFullYear() ? 'numeric' : undefined,
  });
}

// Source badge styles
const sourceBadgeStyles: Record<string, string> = {
  direct: 'bg-accent/10 text-accent',
  discussion: 'bg-blue-100 text-blue-700',
  task: 'bg-green-100 text-green-700',
};

const sourceLabels: Record<string, string> = {
  direct: 'Project',
  discussion: 'Discussion',
  task: 'Task',
};

interface UploadingFile {
  file: File;
  progress: number;
  status: 'uploading' | 'complete' | 'error';
  error?: string;
}

// Build folder tree from flat list
interface FolderTreeNode extends FileFolder {
  childNodes: FolderTreeNode[];
}

function buildFolderTree(folders: FileFolder[]): FolderTreeNode[] {
  const nodeMap = new Map<string, FolderTreeNode>();
  const roots: FolderTreeNode[] = [];

  // Create nodes
  for (const folder of folders) {
    nodeMap.set(folder.id, { ...folder, childNodes: [] });
  }

  // Build tree
  for (const folder of folders) {
    const node = nodeMap.get(folder.id)!;
    if (folder.parentId && nodeMap.has(folder.parentId)) {
      nodeMap.get(folder.parentId)!.childNodes.push(node);
    } else {
      roots.push(node);
    }
  }

  // Sort: system folders first, then alphabetical
  const sortFolders = (a: FolderTreeNode, b: FolderTreeNode) => {
    if (a.isSystem && !b.isSystem) return -1;
    if (!a.isSystem && b.isSystem) return 1;
    return a.name.localeCompare(b.name);
  };

  roots.sort(sortFolders);
  for (const node of nodeMap.values()) {
    node.childNodes.sort(sortFolders);
  }

  return roots;
}

// Get total file count for a folder including all descendants
function getTotalFileCount(node: FolderTreeNode): number {
  let count = node._count?.attachments || 0;
  for (const child of node.childNodes) {
    count += getTotalFileCount(child);
  }
  return count;
}

// Build breadcrumb path from folder ID
function buildBreadcrumb(folderId: string | null, folders: FileFolder[]): FileFolder[] {
  if (!folderId) return [];
  const path: FileFolder[] = [];
  let current = folders.find(f => f.id === folderId);
  while (current) {
    path.unshift(current);
    current = current.parentId ? folders.find(f => f.id === current!.parentId) : undefined;
  }
  return path;
}

// ============================================
// Folder Tree Sidebar Item
// ============================================
function FolderTreeItem({
  node,
  currentFolderId,
  onSelect,
  expandedFolders,
  onToggleExpand,
  dragOverFolderId,
  onDragOverFolder,
  onDragLeaveFolder,
  onDropOnFolder,
  onDragStartFolder,
  onDragEndFolder,
}: {
  node: FolderTreeNode;
  currentFolderId: string | null;
  onSelect: (id: string | null) => void;
  expandedFolders: Set<string>;
  onToggleExpand: (id: string) => void;
  dragOverFolderId: string | null;
  onDragOverFolder: (e: React.DragEvent, folderId: string) => void;
  onDragLeaveFolder: (e: React.DragEvent) => void;
  onDropOnFolder: (e: React.DragEvent, folderId: string) => void;
  onDragStartFolder: (e: React.DragEvent, folderId: string) => void;
  onDragEndFolder: () => void;
}) {
  const isExpanded = expandedFolders.has(node.id);
  const isSelected = currentFolderId === node.id;
  const hasChildren = node.childNodes.length > 0;
  const isDragOver = dragOverFolderId === node.id;

  const FolderIcon = node.isSystem
    ? (node.name === 'Discussions' ? MessageSquare : CheckSquare)
    : Folder;

  return (
    <div>
      <div
        draggable={!node.isSystem}
        onDragStart={(e) => !node.isSystem && onDragStartFolder(e, node.id)}
        onDragEnd={onDragEndFolder}
        onDragOver={(e) => onDragOverFolder(e, node.id)}
        onDragLeave={onDragLeaveFolder}
        onDrop={(e) => onDropOnFolder(e, node.id)}
        onClick={() => onSelect(node.id)}
        className={cn(
          "flex min-w-0 items-center gap-1.5 px-2 py-1.5 rounded-md text-sm cursor-pointer transition-colors group",
          isSelected
            ? "bg-accent/10 text-accent font-medium"
            : "text-grey-700 hover:bg-grey-100",
          isDragOver && "ring-2 ring-accent bg-accent/10",
        )}
      >
        {/* Expand/collapse chevron */}
        <button
          onClick={(e) => {
            e.stopPropagation();
            onToggleExpand(node.id);
          }}
          className={cn(
            "p-0.5 rounded hover:bg-grey-200 transition-colors flex-shrink-0",
            !hasChildren && "invisible"
          )}
        >
          {isExpanded ? (
            <ChevronDown className="w-3.5 h-3.5" />
          ) : (
            <ChevronRight className="w-3.5 h-3.5" />
          )}
        </button>

        <FolderIcon className={cn(
          "w-4 h-4 flex-shrink-0",
          node.isSystem ? "text-accent" : "text-grey-500 group-hover:text-accent",
          isSelected && "text-accent"
        )} />

        <span className="min-w-0 flex-1 truncate">{node.name}</span>

        <span className="text-xs text-grey-400 flex-shrink-0">
          {getTotalFileCount(node)}
        </span>
      </div>

      {/* Children */}
      {isExpanded && hasChildren && (
        <div className="ml-3 border-l border-grey-200 pl-1">
          {node.childNodes.map((child) => (
            <FolderTreeItem
              key={child.id}
              node={child}
              currentFolderId={currentFolderId}
              onSelect={onSelect}
              expandedFolders={expandedFolders}
              onToggleExpand={onToggleExpand}
              dragOverFolderId={dragOverFolderId}
              onDragOverFolder={onDragOverFolder}
              onDragLeaveFolder={onDragLeaveFolder}
              onDropOnFolder={onDropOnFolder}
              onDragStartFolder={onDragStartFolder}
              onDragEndFolder={onDragEndFolder}
            />
          ))}
        </div>
      )}
    </div>
  );
}

// ============================================
// Main Component
// ============================================
export function ProjectFiles() {
  const { projectId } = useParams<{ projectId: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const location = useLocation();
  const nav = useNavigate();
  const queryClient = useQueryClient();
  const { user } = useAuthStore();
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Open file picker when navigated with openCreate state (e.g. from sidebar)
  useEffect(() => {
    if ((location.state as any)?.openCreate) {
      fileInputRef.current?.click();
      nav(location.pathname + location.search, { replace: true, state: {} });
    }
  }, [location.state, location.pathname, location.search, nav]);
  const dragCounter = useRef(0);

  // Staff on THIS project (can manage all files/folders): Solid anywhere, a
  // partner agency on its own projects
  const { isProjectStaff: isAgencyMember } = useProjectRoles(projectId);
  const isAgencyAdmin = user?.organizations?.some(
    (org: any) => org.type === 'AGENCY' && (org.isOwner || org.role === 'Agency Admin')
  );

  const [viewMode, setViewMode] = useState<'list' | 'grid'>('list');
  const [previewFile, setPreviewFile] = useState<ProjectFile | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [uploadingFiles, setUploadingFiles] = useState<UploadingFile[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [currentFolderId, setCurrentFolderId] = useState<string | null>(() => {
    return searchParams.get('folderId') || null;
  });
  const [showCreateFolderModal, setShowCreateFolderModal] = useState(false);
  const [editingFolderId, setEditingFolderId] = useState<string | null>(null);
  const [editingFolderName, setEditingFolderName] = useState('');
  const [folderMenuOpen, setFolderMenuOpen] = useState<string | null>(null);
  const [dragOverFolderId, setDragOverFolderId] = useState<string | null>(null);
  const [isInternalDrag, setIsInternalDrag] = useState(false);
  const [expandedFolders, setExpandedFolders] = useState<Set<string>>(new Set());
  const [draggingType, setDraggingType] = useState<'file' | 'folder' | null>(null);
  const [isTrashView, setIsTrashView] = useState(false);
  const [selectedFileIds, setSelectedFileIds] = useState<Set<string>>(new Set());
  const [selectedFolderIds, setSelectedFolderIds] = useState<Set<string>>(new Set());
  const [lastSelectedFileId, setLastSelectedFileId] = useState<string | null>(null);

  // Sync folder ID with URL
  useEffect(() => {
    const urlFolderId = searchParams.get('folderId');
    if (currentFolderId !== urlFolderId) {
      if (currentFolderId) {
        searchParams.set('folderId', currentFolderId);
      } else {
        searchParams.delete('folderId');
      }
      setSearchParams(searchParams, { replace: true });
    }
  }, [currentFolderId, searchParams, setSearchParams]);

  // Get project details
  const { data: project } = useQuery({
    queryKey: ['project', projectId],
    queryFn: () => projectsApi.get(projectId!),
    enabled: !!projectId,
  });

  const isArchived = !!project?.archivedAt;

  // Get folders for this project
  const { data: folders = [] } = useQuery({
    queryKey: ['project-folders', projectId],
    queryFn: () => attachmentsApi.listFolders(projectId!),
    enabled: !!projectId,
  });

  // Build folder tree
  const folderTree = useMemo(() => buildFolderTree(folders), [folders]);

  // Auto-expand system folders on first load
  useEffect(() => {
    if (folders.length > 0 && expandedFolders.size === 0) {
      const systemIds = new Set(folders.filter(f => f.isSystem).map(f => f.id));
      // Also expand the path to the current folder
      if (currentFolderId) {
        const breadcrumb = buildBreadcrumb(currentFolderId, folders);
        for (const f of breadcrumb) {
          systemIds.add(f.id);
        }
      }
      setExpandedFolders(systemIds);
    }
  }, [folders]); // eslint-disable-line react-hooks/exhaustive-deps

  // Build breadcrumb
  const breadcrumb = useMemo(() => buildBreadcrumb(currentFolderId, folders), [currentFolderId, folders]);

  // Get current folder object
  const currentFolder = currentFolderId ? folders.find(f => f.id === currentFolderId) : null;

  // Build query params - folder-based filtering
  const queryParams: ProjectFilesQuery = {
    folderId: currentFolderId || 'root',
    limit: 200,
    sortBy: 'date',
    sortOrder: 'desc',
  };

  // Get files for active folder
  const { data: filesData, isLoading } = useQuery({
    queryKey: ['project-files', projectId, queryParams],
    queryFn: () => attachmentsApi.listProjectFiles(projectId!, queryParams),
    enabled: !!projectId,
  });

  // Get trash data (always enabled so we can show count in sidebar)
  const { data: trashData } = useQuery({
    queryKey: ['project-trash', projectId],
    queryFn: () => attachmentsApi.listTrash(projectId!),
    enabled: !!projectId,
  });

  const trashCount = (trashData?.files?.length ?? 0) + (trashData?.folders?.length ?? 0);

  const files = filesData?.files || [];

  // Filter files based on search query
  const filteredFiles = files.filter((file: ProjectFile) => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    const nameMatch = file.fileName?.toLowerCase().includes(query);
    const uploaderMatch = file.uploadedBy?.name?.toLowerCase().includes(query);
    return nameMatch || uploaderMatch;
  });

  // Get child folders of current folder for the right panel
  const childFolders = useMemo(() => {
    return folders.filter(f => {
      if (currentFolderId === null) {
        return f.parentId === null;
      }
      return f.parentId === currentFolderId;
    }).sort((a, b) => {
      if (a.isSystem && !b.isSystem) return -1;
      if (!a.isSystem && b.isSystem) return 1;
      return a.name.localeCompare(b.name);
    });
  }, [folders, currentFolderId]);

  // Filter child folders by search
  const filteredChildFolders = childFolders.filter((folder: FileFolder) => {
    if (!searchQuery) return true;
    return folder.name.toLowerCase().includes(searchQuery.toLowerCase());
  });

  // Toggle expand
  const toggleExpand = useCallback((id: string) => {
    setExpandedFolders(prev => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  }, []);

  // Navigate to folder - expand all ancestors in sidebar
  const navigateToFolder = useCallback((id: string | null) => {
    setCurrentFolderId(id);
    setSearchQuery('');
    setIsTrashView(false);
    if (id) {
      const path = buildBreadcrumb(id, folders);
      setExpandedFolders(prev => {
        const next = new Set(prev);
        for (const f of path) {
          next.add(f.id);
        }
        return next;
      });
    }
  }, [folders]);

  // Folder mutations
  const renameFolderMutation = useMutation({
    mutationFn: ({ folderId, name }: { folderId: string; name: string }) =>
      attachmentsApi.renameFolder(projectId!, folderId, name),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['project-folders', projectId] });
      setEditingFolderId(null);
      setEditingFolderName('');
    },
  });

  const deleteFolderMutation = useMutation({
    mutationFn: (folderId: string) => attachmentsApi.deleteFolder(projectId!, folderId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['project-folders', projectId] });
      queryClient.invalidateQueries({ queryKey: ['project-files', projectId] });
      if (currentFolderId) setCurrentFolderId(null);
    },
  });

  const moveFileMutation = useMutation({
    mutationFn: ({ fileId, folderId }: { fileId: string; folderId: string | null }) =>
      attachmentsApi.moveFile(fileId, folderId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['project-files', projectId] });
      queryClient.invalidateQueries({ queryKey: ['project-folders', projectId] });
    },
  });

  const moveFolderMutation = useMutation({
    mutationFn: ({ folderId, parentId }: { folderId: string; parentId: string | null }) =>
      attachmentsApi.moveFolder(projectId!, folderId, parentId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['project-folders', projectId] });
    },
  });

  const deleteFileMutation = useMutation({
    mutationFn: (fileId: string) => attachmentsApi.delete(fileId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['project-files', projectId] });
      queryClient.invalidateQueries({ queryKey: ['project-folders', projectId] });
    },
  });

  const trashFileMutation = useMutation({
    mutationFn: (fileId: string) => attachmentsApi.trashFile(fileId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['project-files', projectId] });
      queryClient.invalidateQueries({ queryKey: ['project-folders', projectId] });
      queryClient.invalidateQueries({ queryKey: ['project-trash', projectId] });
    },
  });

  const trashFolderMutation = useMutation({
    mutationFn: (folderId: string) => attachmentsApi.trashFolder(folderId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['project-files', projectId] });
      queryClient.invalidateQueries({ queryKey: ['project-folders', projectId] });
      queryClient.invalidateQueries({ queryKey: ['project-trash', projectId] });
      if (currentFolderId) setCurrentFolderId(null);
    },
  });

  const bulkTrashMutation = useMutation({
    mutationFn: ({ fileIds, folderIds }: { fileIds: string[]; folderIds: string[] }) =>
      attachmentsApi.bulkTrash(projectId!, fileIds, folderIds),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['project-files', projectId] });
      queryClient.invalidateQueries({ queryKey: ['project-folders', projectId] });
      queryClient.invalidateQueries({ queryKey: ['project-trash', projectId] });
      setSelectedFileIds(new Set());
      setSelectedFolderIds(new Set());
    },
  });

  const emptyTrashMutation = useMutation({
    mutationFn: () => attachmentsApi.emptyTrash(projectId!),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['project-trash', projectId] });
      queryClient.invalidateQueries({ queryKey: ['project-files', projectId] });
      queryClient.invalidateQueries({ queryKey: ['project-folders', projectId] });
    },
  });

  const restoreFileMutation = useMutation({
    mutationFn: (fileId: string) => attachmentsApi.restoreFile(fileId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['project-trash', projectId] });
      queryClient.invalidateQueries({ queryKey: ['project-files', projectId] });
      queryClient.invalidateQueries({ queryKey: ['project-folders', projectId] });
    },
  });

  const restoreFolderMutation = useMutation({
    mutationFn: (folderId: string) => attachmentsApi.restoreFolder(folderId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['project-trash', projectId] });
      queryClient.invalidateQueries({ queryKey: ['project-files', projectId] });
      queryClient.invalidateQueries({ queryKey: ['project-folders', projectId] });
    },
  });

  // Handle file upload
  const handleFileUpload = useCallback(async (filesToUpload: FileList | null) => {
    if (!filesToUpload || !projectId || !project?.organizationId) return;

    const newUploads: UploadingFile[] = Array.from(filesToUpload).map(file => ({
      file,
      progress: 0,
      status: 'uploading' as const,
    }));

    setUploadingFiles(prev => [...prev, ...newUploads]);

    for (let i = 0; i < filesToUpload.length; i++) {
      const file = filesToUpload[i];

      try {
        await attachmentsApi.uploadFile(
          file,
          project.organizationId,
          'project',
          projectId,
          (progress) => {
            setUploadingFiles(prev =>
              prev.map(u =>
                u.file === file ? { ...u, progress } : u
              )
            );
          },
          currentFolderId || undefined // Upload to current folder
        );

        setUploadingFiles(prev =>
          prev.map(u =>
            u.file === file ? { ...u, status: 'complete' as const, progress: 100 } : u
          )
        );
      } catch (error) {
        setUploadingFiles(prev =>
          prev.map(u =>
            u.file === file ? { ...u, status: 'error' as const, error: 'Upload failed' } : u
          )
        );
      }
    }

    // Refresh file list and folder counts
    queryClient.invalidateQueries({ queryKey: ['project-files', projectId] });
    queryClient.invalidateQueries({ queryKey: ['project-folders', projectId] });

    // Reset file input so the same file can be uploaded again
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }

    // Clear completed uploads after a delay
    setTimeout(() => {
      setUploadingFiles(prev => prev.filter(u => u.status === 'uploading'));
    }, 3000);
  }, [projectId, project?.organizationId, queryClient, currentFolderId]);

  // Handle trash folder with confirmation
  const handleTrashFolder = (folder: FileFolder) => {
    const fileCount = folder._count?.totalFiles ?? folder._count?.attachments ?? 0;
    const childCount = folder._count?.children || 0;
    const message = fileCount > 0 || childCount > 0
      ? `Move this folder and its ${fileCount} file${fileCount === 1 ? '' : 's'} and ${childCount} sub-folder${childCount === 1 ? '' : 's'} to Trash?`
      : 'Move this folder to Trash?';

    if (window.confirm(message)) {
      trashFolderMutation.mutate(folder.id);
    }
  };

  // Handle trash file with confirmation
  const handleTrashFile = (file: ProjectFile) => {
    if (window.confirm(`Move "${file.fileName}" to Trash?`)) {
      trashFileMutation.mutate(file.id);
    }
  };

  // Handle empty trash
  const handleEmptyTrash = () => {
    if (window.confirm('Permanently delete all items in Trash? This cannot be undone.')) {
      emptyTrashMutation.mutate();
    }
  };

  // Selection handlers
  const clearSelection = useCallback(() => {
    setSelectedFileIds(new Set());
    setSelectedFolderIds(new Set());
    setLastSelectedFileId(null);
  }, []);

  // Reset selection when changing folders or switching to trash view
  useEffect(() => {
    clearSelection();
  }, [currentFolderId, isTrashView, clearSelection]);

  const handleFileCheckbox = useCallback((fileId: string, e: MouseEvent) => {
    e.stopPropagation();

    if (e.shiftKey && lastSelectedFileId) {
      // Shift-click range select
      const visibleIds = filteredFiles.map(f => f.id);
      const lastIdx = visibleIds.indexOf(lastSelectedFileId);
      const currentIdx = visibleIds.indexOf(fileId);
      if (lastIdx !== -1 && currentIdx !== -1) {
        const start = Math.min(lastIdx, currentIdx);
        const end = Math.max(lastIdx, currentIdx);
        setSelectedFileIds(prev => {
          const next = new Set(prev);
          for (let i = start; i <= end; i++) {
            next.add(visibleIds[i]);
          }
          return next;
        });
        return;
      }
    }

    setSelectedFileIds(prev => {
      const next = new Set(prev);
      if (next.has(fileId)) {
        next.delete(fileId);
      } else {
        next.add(fileId);
      }
      return next;
    });
    setLastSelectedFileId(fileId);
  }, [lastSelectedFileId, filteredFiles]);

  const handleFolderCheckbox = useCallback((folderId: string, e: MouseEvent) => {
    e.stopPropagation();
    setSelectedFolderIds(prev => {
      const next = new Set(prev);
      if (next.has(folderId)) {
        next.delete(folderId);
      } else {
        next.add(folderId);
      }
      return next;
    });
  }, []);

  const handleSelectAll = useCallback(() => {
    if (selectedFileIds.size === filteredFiles.length && selectedFolderIds.size === filteredChildFolders.filter(f => !f.isSystem).length) {
      clearSelection();
    } else {
      setSelectedFileIds(new Set(filteredFiles.map(f => f.id)));
      setSelectedFolderIds(new Set(filteredChildFolders.filter(f => !f.isSystem).map(f => f.id)));
    }
  }, [filteredFiles, filteredChildFolders, selectedFileIds.size, selectedFolderIds.size, clearSelection]);

  const hasSelection = selectedFileIds.size > 0 || selectedFolderIds.size > 0;

  // Bulk download handler
  const handleBulkDownload = async () => {
    if (selectedFileIds.size === 0) return;
    try {
      const blob = await attachmentsApi.bulkDownload(projectId!, Array.from(selectedFileIds));
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const dateStr = new Date().toISOString().split('T')[0];
      a.download = `files-${dateStr}.zip`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Bulk download failed:', error);
    }
  };

  // Bulk trash handler
  const handleBulkTrash = () => {
    const count = selectedFileIds.size + selectedFolderIds.size;
    if (window.confirm(`Move ${count} item${count === 1 ? '' : 's'} to Trash?`)) {
      bulkTrashMutation.mutate({
        fileIds: Array.from(selectedFileIds),
        folderIds: Array.from(selectedFolderIds),
      });
    }
  };

  // Drag and drop for files (internal moves)
  const handleFileDragStart = (e: React.DragEvent, fileId: string) => {
    e.dataTransfer.setData('fileId', fileId);
    e.dataTransfer.effectAllowed = 'move';
    setIsInternalDrag(true);
    setDraggingType('file');
  };

  const handleFolderDragStart = (e: React.DragEvent, folderId: string) => {
    e.dataTransfer.setData('folderId', folderId);
    e.dataTransfer.effectAllowed = 'move';
    setIsInternalDrag(true);
    setDraggingType('folder');
  };

  const handleDragEnd = () => {
    setIsInternalDrag(false);
    setDragOverFolderId(null);
    setDraggingType(null);
  };

  const handleFolderDragOver = (e: React.DragEvent, folderId: string) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOverFolderId(folderId);
  };

  const handleFolderDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOverFolderId(null);
  };

  const handleFolderDrop = (e: React.DragEvent, targetFolderId: string | null) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOverFolderId(null);
    setIsInternalDrag(false);
    setDraggingType(null);

    const fileId = e.dataTransfer.getData('fileId');
    const folderId = e.dataTransfer.getData('folderId');

    // Special handling for trash drop target
    if (targetFolderId === '__trash__') {
      // If the dragged item is part of a multi-selection, trash everything selected
      if (fileId && selectedFileIds.has(fileId) && (selectedFileIds.size > 1 || selectedFolderIds.size > 0)) {
        bulkTrashMutation.mutate({
          fileIds: Array.from(selectedFileIds),
          folderIds: Array.from(selectedFolderIds),
        });
      } else if (folderId && selectedFolderIds.has(folderId) && (selectedFileIds.size > 0 || selectedFolderIds.size > 1)) {
        bulkTrashMutation.mutate({
          fileIds: Array.from(selectedFileIds),
          folderIds: Array.from(selectedFolderIds),
        });
      } else if (fileId) {
        trashFileMutation.mutate(fileId);
      } else if (folderId) {
        trashFolderMutation.mutate(folderId);
      }
      return;
    }

    if (fileId) {
      // If the dragged file is part of a multi-selection, move all selected items
      if (selectedFileIds.has(fileId) && (selectedFileIds.size > 1 || selectedFolderIds.size > 0)) {
        // Bulk move: move all selected files
        for (const id of selectedFileIds) {
          moveFileMutation.mutate({ fileId: id, folderId: targetFolderId });
        }
        // Bulk move: move all selected folders (skip moving a folder into itself)
        for (const id of selectedFolderIds) {
          if (id !== targetFolderId) {
            moveFolderMutation.mutate({ folderId: id, parentId: targetFolderId });
          }
        }
        clearSelection();
      } else {
        // Single file move
        moveFileMutation.mutate({ fileId, folderId: targetFolderId });
        clearSelection();
      }
    } else if (folderId) {
      // If the dragged folder is part of a multi-selection, move all selected items
      if (selectedFolderIds.has(folderId) && (selectedFileIds.size > 0 || selectedFolderIds.size > 1)) {
        // Bulk move: move all selected files
        for (const id of selectedFileIds) {
          moveFileMutation.mutate({ fileId: id, folderId: targetFolderId });
        }
        // Bulk move: move all selected folders (skip moving a folder into itself)
        for (const id of selectedFolderIds) {
          if (id !== targetFolderId) {
            moveFolderMutation.mutate({ folderId: id, parentId: targetFolderId });
          }
        }
        clearSelection();
      } else if (folderId !== targetFolderId) {
        // Single folder move
        moveFolderMutation.mutate({ folderId, parentId: targetFolderId });
        clearSelection();
      }
    }
  };

  // Drag and drop handlers (for file uploads from outside)
  const handleDragEnter = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (isInternalDrag) return;
    dragCounter.current++;
    if (e.dataTransfer.items && e.dataTransfer.items.length > 0) {
      setIsDragging(true);
    }
  }, [isInternalDrag]);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounter.current--;
    if (dragCounter.current === 0) {
      setIsDragging(false);
    }
  }, []);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    dragCounter.current = 0;

    if (isInternalDrag) {
      setIsInternalDrag(false);
      return;
    }

    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      handleFileUpload(e.dataTransfer.files);
      e.dataTransfer.clearData();
    }
  }, [handleFileUpload, isInternalDrag]);

  // Handle download via server-streamed endpoint (avoids CORS/opaque-cache issues)
  const handleDownload = async (file: ProjectFile) => {
    try {
      const downloadUrl = attachmentsApi.getServerDownloadUrl(file.id);
      const token = JSON.parse(localStorage.getItem('solid-hub-auth') || '{}')?.state?.token;
      const response = await fetch(downloadUrl, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      const blob = await response.blob();
      const blobUrl = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = blobUrl;
      a.download = file.fileName;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(blobUrl);
    } catch (error) {
      console.error('Download failed:', error);
    }
  };

  return (
    <div
      className="relative"
      onDragEnter={!isArchived ? handleDragEnter : undefined}
      onDragLeave={!isArchived ? handleDragLeave : undefined}
      onDragOver={!isArchived ? handleDragOver : undefined}
      onDrop={!isArchived ? handleDrop : undefined}
    >
      {/* Drag overlay for external file uploads */}
      {isDragging && (
        <div className="fixed inset-0 z-50 bg-accent/10 border-4 border-dashed border-accent flex items-center justify-center">
          <div className="text-center bg-white rounded-lg p-8 shadow-lg">
            <Upload className="w-12 h-12 text-accent mx-auto mb-3" />
            <p className="text-lg font-medium text-accent">Drop files to upload</p>
          </div>
        </div>
      )}

      {/* Upload progress */}
      {uploadingFiles.length > 0 && (
        <Card className="p-4 mb-4">
          <div className="space-y-2">
            {uploadingFiles.map((upload, idx) => (
              <div key={idx} className="flex items-center gap-3">
                {upload.status === 'uploading' && (
                  <Loader2 className="w-4 h-4 text-accent animate-spin" />
                )}
                {upload.status === 'complete' && (
                  <CheckCircle2 className="w-4 h-4 text-success" />
                )}
                {upload.status === 'error' && (
                  <AlertCircle className="w-4 h-4 text-error" />
                )}
                <span className="truncate flex-1 text-sm">{upload.file.name}</span>
                <span className="text-xs text-grey-500">
                  {upload.status === 'uploading' ? `${upload.progress}%` : upload.status}
                </span>
              </div>
            ))}
          </div>
        </Card>
      )}

      {/* Two-panel layout */}
      <div className="flex flex-col sm:flex-row gap-4" style={{ minHeight: '500px' }}>
        {/* Left Sidebar: Folder Tree */}
        {/* self-start is a DESKTOP rule: it keeps the sticky sidebar from
            stretching the row's full height. On a phone the panel is stacked,
            and self-start made it size to its CONTENT — so the longest folder
            name set the page width (493px in a 390px viewport, 2026-08-28).
            Below sm it stretches and its names truncate. */}
        <div className="w-full min-w-0 sm:w-64 sm:flex-shrink-0 sm:self-start sm:sticky sm:top-4">
          <Card className="p-3 max-h-[calc(100vh-8rem)] overflow-y-auto">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-sm font-semibold text-secondary">Folders</h3>
              {!isArchived && (
                <button
                  onClick={() => setShowCreateFolderModal(true)}
                  className="p-1 hover:bg-grey-100 rounded transition-colors"
                  title="New Folder"
                >
                  <FolderPlus className="w-4 h-4 text-grey-500 hover:text-accent" />
                </button>
              )}
            </div>

            {/* Root ("All Files") */}
            <div
              onClick={() => navigateToFolder(null)}
              onDragOver={(e) => { e.preventDefault(); setDragOverFolderId('root'); }}
              onDragLeave={() => setDragOverFolderId(null)}
              onDrop={(e) => handleFolderDrop(e, null)}
              className={cn(
                "flex items-center gap-1.5 px-2 py-1.5 rounded-md text-sm cursor-pointer transition-colors mb-1",
                currentFolderId === null
                  ? "bg-accent/10 text-accent font-medium"
                  : "text-grey-700 hover:bg-grey-100",
                dragOverFolderId === 'root' && "ring-2 ring-accent bg-accent/10"
              )}
            >
              <Folder className="w-4 h-4 ml-5" />
              <span className="flex-1">All Files</span>
            </div>

            {/* Folder tree */}
            <div className="space-y-0.5">
              {folderTree.map((node) => (
                <FolderTreeItem
                  key={node.id}
                  node={node}
                  currentFolderId={currentFolderId}
                  onSelect={navigateToFolder}
                  expandedFolders={expandedFolders}
                  onToggleExpand={toggleExpand}
                  dragOverFolderId={dragOverFolderId}
                  onDragOverFolder={handleFolderDragOver}
                  onDragLeaveFolder={handleFolderDragLeave}
                  onDropOnFolder={handleFolderDrop}
                  onDragStartFolder={handleFolderDragStart}
                  onDragEndFolder={handleDragEnd}
                />
              ))}
            </div>

            {/* Trash sidebar item */}
            <div className="border-t border-grey-200 mt-3 pt-3">
              <div
                onClick={() => {
                  setIsTrashView(true);
                  setCurrentFolderId(null);
                  setSearchQuery('');
                }}
                onDragOver={(e) => { e.preventDefault(); setDragOverFolderId('__trash__'); }}
                onDragLeave={() => setDragOverFolderId(null)}
                onDrop={(e) => handleFolderDrop(e, '__trash__')}
                className={cn(
                  "flex items-center gap-1.5 px-2 py-1.5 rounded-md text-sm cursor-pointer transition-colors",
                  isTrashView
                    ? "bg-accent/10 text-accent font-medium"
                    : "text-grey-700 hover:bg-grey-100",
                  dragOverFolderId === '__trash__' && "ring-2 ring-error bg-error/10",
                )}
              >
                <Trash2 className={cn(
                  "w-4 h-4 ml-5",
                  dragOverFolderId === '__trash__' ? "text-error" : ""
                )} />
                <span className="flex-1">Trash</span>
                {trashCount > 0 && (
                  <span className="text-xs text-grey-400 flex-shrink-0">{trashCount}</span>
                )}
              </div>
            </div>
          </Card>
        </div>

        {/* Right Panel: Files */}
        <div className="flex-1 min-w-0">
          <Card className="h-full">
            {/* Breadcrumb */}
            <div className="flex items-center gap-2 text-sm mb-4">
              <button
                onClick={() => navigateToFolder(null)}
                className={cn(
                  "font-medium transition-colors",
                  !currentFolderId && !isTrashView ? "text-secondary" : "text-accent hover:underline"
                )}
              >
                Files
              </button>
              {isTrashView ? (
                <span className="flex items-center gap-2">
                  <ChevronRight className="w-4 h-4 text-grey-400" />
                  <span className="font-medium text-secondary">Trash</span>
                </span>
              ) : breadcrumb.map((folder) => (
                <span key={folder.id} className="flex items-center gap-2">
                  <ChevronRight className="w-4 h-4 text-grey-400" />
                  <button
                    onClick={() => navigateToFolder(folder.id)}
                    className={cn(
                      "font-medium transition-colors",
                      folder.id === currentFolderId ? "text-secondary" : "text-accent hover:underline"
                    )}
                  >
                    {folder.name}
                  </button>
                </span>
              ))}
            </div>

            {/* Bulk action toolbar (shown when items selected) */}
            {hasSelection && !isTrashView && (
              <div className="flex items-center gap-3 mb-4 p-3 bg-accent/5 border border-accent/20 rounded-lg">
                <button
                  onClick={clearSelection}
                  className="p-1 hover:bg-grey-100 rounded"
                  title="Clear selection"
                >
                  <X className="w-4 h-4 text-grey-500" />
                </button>
                <span className="text-sm font-medium text-secondary">
                  {selectedFileIds.size + selectedFolderIds.size} selected
                </span>
                <div className="flex-1" />
                {selectedFileIds.size > 0 && (
                  <Button variant="outline" size="sm" onClick={handleBulkDownload}>
                    <Download className="w-4 h-4" />
                    Download as Zip
                  </Button>
                )}
                {isAgencyMember && selectedFileIds.size > 0 && (
                  <Button variant="outline" size="sm" onClick={() => {
                    // Move to folder - use existing folder navigation to pick
                    const folderId = prompt('Enter folder ID to move files to (or leave empty for root):');
                    if (folderId !== null) {
                      const targetId = folderId.trim() || null;
                      for (const fileId of selectedFileIds) {
                        moveFileMutation.mutate({ fileId, folderId: targetId });
                      }
                      clearSelection();
                    }
                  }}>
                    <Folder className="w-4 h-4" />
                    Move to Folder
                  </Button>
                )}
                {(isAgencyMember || selectedFolderIds.size === 0) && (
                  <Button variant="outline" size="sm" onClick={handleBulkTrash} className="text-error border-error/30 hover:bg-error/5">
                    <Trash2 className="w-4 h-4" />
                    Move to Trash
                  </Button>
                )}
              </div>
            )}

            {/* Action Bar */}
            {!isTrashView && (
            <div className="flex flex-wrap items-center gap-2 sm:gap-3 mb-4">
              {/* Search */}
              <div className="relative flex-1 min-w-[140px] sm:flex-none sm:w-64">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-grey-400" />
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search files..."
                  className="input pl-10 pr-8 text-sm w-full"
                />
                {searchQuery && (
                  <button
                    onClick={() => setSearchQuery('')}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-grey-400 hover:text-grey-600"
                  >
                    <X className="w-4 h-4" />
                  </button>
                )}
              </div>

              <div className="hidden sm:block flex-1" />

              {/* View toggle */}
              <div className="flex items-center bg-grey-100 rounded-lg p-1">
                <button
                  onClick={() => setViewMode('list')}
                  className={cn(
                    "p-1.5 rounded transition-colors",
                    viewMode === 'list' ? "bg-white shadow-sm text-secondary" : "text-grey-500 hover:text-secondary"
                  )}
                  title="List view"
                >
                  <List className="w-4 h-4" />
                </button>
                <button
                  onClick={() => setViewMode('grid')}
                  className={cn(
                    "p-1.5 rounded transition-colors",
                    viewMode === 'grid' ? "bg-white shadow-sm text-secondary" : "text-grey-500 hover:text-secondary"
                  )}
                  title="Grid view"
                >
                  <Grid className="w-4 h-4" />
                </button>
              </div>

              {!isArchived && (
                <Button variant="outline" size="sm" onClick={() => setShowCreateFolderModal(true)}>
                  <FolderPlus className="w-4 h-4" />
                  <span className="hidden sm:inline">New Folder</span>
                </Button>
              )}

              {!isArchived && (
                <Button size="sm" onClick={() => fileInputRef.current?.click()}>
                  <Upload className="w-4 h-4" />
                  <span className="hidden sm:inline">Upload Files</span>
                </Button>
              )}
              <input
                ref={fileInputRef}
                type="file"
                multiple
                className="hidden"
                onChange={(e) => handleFileUpload(e.target.files)}
              />
            </div>
            )}

            {/* Trash view */}
            {isTrashView && (
              <div>
                {/* Empty Trash button (agency admin only) */}
                {isAgencyAdmin && (trashData?.files?.length || trashData?.folders?.length) && (
                  <div className="flex justify-end mb-4">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={handleEmptyTrash}
                      disabled={emptyTrashMutation.isPending}
                      className="text-error border-error/30 hover:bg-error/5"
                    >
                      <Trash2 className="w-4 h-4" />
                      {emptyTrashMutation.isPending ? 'Emptying...' : 'Empty Trash'}
                    </Button>
                  </div>
                )}

                {/* Trashed folders */}
                {(trashData?.folders?.length ?? 0) > 0 && (
                  <div className="mb-4">
                    <h4 className="text-xs font-semibold text-grey-500 uppercase mb-2">Folders</h4>
                    <div className="divide-y divide-grey-100">
                      {trashData!.folders.map((folder: any) => (
                        <div
                          key={folder.id}
                          className="flex items-center gap-4 py-3 px-2 text-grey-500 group"
                        >
                          <Folder className="w-5 h-5 flex-shrink-0" />
                          <div className="flex-1 min-w-0">
                            <p className="font-medium text-secondary truncate">{folder.name}</p>
                            <p className="text-xs text-grey-400">
                              Trashed {formatDate(folder.trashedAt)}
                              {folder.trashedByName && ` by ${folder.trashedByName}`}
                            </p>
                          </div>
                          <button
                            onClick={() => restoreFolderMutation.mutate(folder.id)}
                            disabled={restoreFolderMutation.isPending}
                            className="p-2 hover:bg-accent/10 rounded opacity-0 group-hover:opacity-100 transition-all"
                            title="Restore folder"
                          >
                            <RotateCcw className="w-4 h-4 text-accent" />
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Trashed files */}
                {(trashData?.files?.length ?? 0) > 0 && (
                  <div>
                    <h4 className="text-xs font-semibold text-grey-500 uppercase mb-2">Files</h4>
                    <div className="divide-y divide-grey-100">
                      {trashData!.files.map((file: any) => {
                        const Icon = getMimeCategoryIcon(file.mimeCategory, file.mimeType);
                        return (
                          <div
                            key={file.id}
                            className="flex items-center gap-4 py-3 px-2 text-grey-500 group"
                          >
                            <Icon className="w-5 h-5 flex-shrink-0" />
                            <div className="flex-1 min-w-0">
                              <p className="font-medium text-secondary truncate">{file.fileName}</p>
                              <p className="text-xs text-grey-400">
                                {formatBytes(file.fileSize)} · Trashed {formatDate(file.trashedAt)}
                                {file.trashedByName && ` by ${file.trashedByName}`}
                              </p>
                            </div>
                            <button
                              onClick={() => restoreFileMutation.mutate(file.id)}
                              disabled={restoreFileMutation.isPending}
                              className="p-2 hover:bg-accent/10 rounded opacity-0 group-hover:opacity-100 transition-all"
                              title="Restore file"
                            >
                              <RotateCcw className="w-4 h-4 text-accent" />
                            </button>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* Empty trash state */}
                {(!trashData?.files?.length && !trashData?.folders?.length) && (
                  <div className="py-16 text-center text-grey-500">
                    <Trash2 className="w-8 h-8 mx-auto mb-3 text-grey-300" />
                    <p className="font-medium">Trash is empty</p>
                    <p className="text-sm mt-1">Deleted files will appear here for 30 days</p>
                  </div>
                )}
              </div>
            )}

            {/* Child folders in right panel */}
            {!isTrashView && filteredChildFolders.length > 0 && (
              <div className="mb-4">
                <div className="grid grid-cols-1 xs:grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
                  {filteredChildFolders.map((folder) => {
                    const FolderIcon = folder.isSystem
                      ? (folder.name === 'Discussions' ? MessageSquare : folder.name === 'Tasks' ? CheckSquare : Folder)
                      : Folder;

                    return (
                      <div
                        key={folder.id}
                        draggable={!folder.isSystem}
                        onDragStart={(e) => !folder.isSystem && handleFolderDragStart(e, folder.id)}
                        onDragEnd={handleDragEnd}
                        onDragOver={(e) => handleFolderDragOver(e, folder.id)}
                        onDragLeave={handleFolderDragLeave}
                        onDrop={(e) => handleFolderDrop(e, folder.id)}
                        className={cn(
                          "group relative flex items-center gap-3 p-3 rounded-lg border border-grey-200 hover:border-accent/50 hover:bg-accent/5 transition-all cursor-pointer",
                          dragOverFolderId === folder.id && "ring-2 ring-accent bg-accent/10 border-accent",
                          !folder.isSystem && "cursor-grab active:cursor-grabbing"
                        )}
                        onClick={() => {
                          if (editingFolderId !== folder.id) {
                            navigateToFolder(folder.id);
                          }
                        }}
                      >
                        {/* Checkbox for non-system folders */}
                        {!folder.isSystem && !isArchived && (
                          <input
                            type="checkbox"
                            checked={selectedFolderIds.has(folder.id)}
                            onClick={(e) => handleFolderCheckbox(folder.id, e as unknown as MouseEvent)}
                            onChange={() => {}}
                            className={cn(
                              "w-4 h-4 rounded border-grey-300 text-accent flex-shrink-0 cursor-pointer",
                              hasSelection ? "opacity-100" : "opacity-0 group-hover:opacity-100"
                            )}
                          />
                        )}
                        <FolderIcon className={cn(
                          "w-8 h-8 flex-shrink-0",
                          folder.isSystem ? "text-accent" : "text-accent/70"
                        )} />
                        <div className="flex-1 min-w-0">
                          {editingFolderId === folder.id ? (
                            <input
                              type="text"
                              value={editingFolderName}
                              onChange={(e) => setEditingFolderName(e.target.value)}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter') {
                                  renameFolderMutation.mutate({ folderId: folder.id, name: editingFolderName });
                                } else if (e.key === 'Escape') {
                                  setEditingFolderId(null);
                                }
                              }}
                              onBlur={() => {
                                if (editingFolderName.trim() && editingFolderName !== folder.name) {
                                  renameFolderMutation.mutate({ folderId: folder.id, name: editingFolderName });
                                } else {
                                  setEditingFolderId(null);
                                }
                              }}
                              onClick={(e) => e.stopPropagation()}
                              className="w-full px-1 py-0.5 text-sm border border-accent rounded focus:outline-none"
                              autoFocus
                            />
                          ) : (
                            <>
                              <p className="font-medium text-secondary truncate text-sm">{folder.name}</p>
                              <p className="text-xs text-grey-500">
                                {folder._count?.totalFiles ?? folder._count?.attachments ?? 0} files
                                {(folder._count?.children || 0) > 0 && `, ${folder._count?.children} folders`}
                              </p>
                            </>
                          )}
                        </div>
                        {/* Folder menu - only for non-system folders, agency members or folder creator */}
                        {!folder.isSystem && (isAgencyMember || folder.createdBy?.id === user?.id) && (
                          <div className="relative">
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                setFolderMenuOpen(folderMenuOpen === folder.id ? null : folder.id);
                              }}
                              className="p-1 opacity-0 group-hover:opacity-100 hover:bg-grey-100 rounded transition-all"
                            >
                              <MoreVertical className="w-4 h-4 text-grey-500" />
                            </button>
                            {folderMenuOpen === folder.id && (
                              <div className="absolute right-0 top-full mt-1 w-36 bg-white border border-grey-200 rounded-lg shadow-lg z-10">
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setEditingFolderId(folder.id);
                                    setEditingFolderName(folder.name);
                                    setFolderMenuOpen(null);
                                  }}
                                  className="w-full flex items-center gap-2 px-3 py-2 text-sm text-grey-700 hover:bg-grey-50"
                                >
                                  <Edit2 className="w-4 h-4" />
                                  Rename
                                </button>
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setFolderMenuOpen(null);
                                    handleTrashFolder(folder);
                                  }}
                                  className="w-full flex items-center gap-2 px-3 py-2 text-sm text-error hover:bg-error/5"
                                >
                                  <Trash2 className="w-4 h-4" />
                                  Move to Trash
                                </button>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Files */}
            {isTrashView ? null : isLoading ? (
              <div className="flex items-center justify-center py-16">
                <Loader2 className="w-8 h-8 text-grey-400 animate-spin" />
              </div>
            ) : filteredFiles.length === 0 && filteredChildFolders.length === 0 ? (
              // Empty state with upload prompt
              <div
                className="border-2 border-dashed border-grey-200 rounded-lg p-8 text-center cursor-pointer hover:border-accent/50 hover:bg-accent/5 transition-colors"
                onClick={() => !isArchived && fileInputRef.current?.click()}
              >
                <Upload className="w-8 h-8 text-grey-400 mx-auto mb-3" />
                <p className="text-secondary font-medium mb-1">
                  {isArchived ? 'No files in this folder' : 'Click to select a file'}
                </p>
                {!isArchived && <p className="text-sm text-grey-500">or drag and drop files</p>}
              </div>
            ) : filteredFiles.length === 0 && searchQuery ? (
              <div className="py-8 text-center text-grey-500">
                <p>No files matching "{searchQuery}"</p>
              </div>
            ) : filteredFiles.length === 0 ? null : viewMode === 'list' ? (
              /* List View */
              <div className="divide-y divide-grey-100">
                {/* Select all header */}
                {!isArchived && filteredFiles.length > 0 && (
                  <div className="flex items-center gap-4 py-2 px-2 text-xs text-grey-500">
                    <input
                      type="checkbox"
                      checked={selectedFileIds.size === filteredFiles.length && filteredFiles.length > 0}
                      onChange={handleSelectAll}
                      className="w-4 h-4 rounded border-grey-300 text-accent cursor-pointer"
                    />
                    <span>Select all</span>
                  </div>
                )}
                {filteredFiles.map((file) => {
                  const Icon = getMimeCategoryIcon(file.mimeCategory, file.mimeType);
                  const canMoveFile = isAgencyMember || file.uploadedBy?.id === user?.id;
                  const isSelected = selectedFileIds.has(file.id);

                  return (
                    <div
                      key={file.id}
                      draggable={canMoveFile}
                      onDragStart={(e) => canMoveFile && handleFileDragStart(e, file.id)}
                      onDragEnd={handleDragEnd}
                      onClick={(e) => {
                        if (!isArchived) {
                          handleFileCheckbox(file.id, e as unknown as MouseEvent);
                        } else {
                          setPreviewFile(file);
                        }
                      }}
                      className={cn(
                        "flex items-center gap-4 py-3 px-2 hover:bg-grey-50 transition-colors cursor-pointer group",
                        canMoveFile && "cursor-grab active:cursor-grabbing",
                        isSelected && "bg-accent/5"
                      )}
                    >
                      {/* Checkbox */}
                      {!isArchived && (
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onClick={(e) => handleFileCheckbox(file.id, e as unknown as MouseEvent)}
                          onMouseDown={(e) => e.stopPropagation()}
                          onChange={() => {}}
                          className={cn(
                            "w-4 h-4 rounded border-grey-300 text-accent flex-shrink-0 cursor-pointer",
                            hasSelection ? "opacity-100" : "opacity-0 group-hover:opacity-100"
                          )}
                        />
                      )}
                      <div className="w-10 h-10 rounded-lg bg-grey-100 flex items-center justify-center flex-shrink-0 overflow-hidden" onClick={(e) => { e.stopPropagation(); setPreviewFile(file); }}>
                        <ImageThumbnail file={file} />
                      </div>

                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-secondary truncate cursor-pointer hover:text-accent hover:underline" onClick={(e) => { e.stopPropagation(); setPreviewFile(file); }}>{file.fileName}</p>
                        <div className="flex items-center gap-2 text-xs text-grey-500">
                          <span onClick={(e) => { e.stopPropagation(); setPreviewFile(file); }} className="cursor-pointer hover:text-secondary">{formatBytes(file.fileSize)}</span>
                          <span>·</span>
                          <span onClick={(e) => { e.stopPropagation(); setPreviewFile(file); }} className="cursor-pointer hover:text-secondary">{formatDate(file.createdAt)}</span>
                          {file.uploadedBy && (
                            <>
                              <span>·</span>
                              <span onClick={(e) => { e.stopPropagation(); setPreviewFile(file); }} className="cursor-pointer hover:text-secondary">{file.uploadedBy.name}</span>
                            </>
                          )}
                          <span
                            onClick={(e) => { e.stopPropagation(); setPreviewFile(file); }}
                            className={cn(
                              "px-1.5 py-0.5 rounded text-[10px] font-medium cursor-pointer",
                              sourceBadgeStyles[file.source]
                            )}
                          >
                            {sourceLabels[file.source]}
                          </span>
                        </div>
                      </div>

                      <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-all">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDownload(file);
                          }}
                          className="p-2 hover:bg-grey-100 rounded"
                          title="Download"
                        >
                          <Download className="w-4 h-4 text-grey-500" />
                        </button>
                        {(isAgencyMember || file.uploadedBy?.id === user?.id) && !isArchived && (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleTrashFile(file);
                            }}
                            className="p-2 hover:bg-error/10 rounded"
                            title="Move to Trash"
                          >
                            <Trash2 className="w-4 h-4 text-grey-500 hover:text-error" />
                          </button>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              /* Grid View */
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
                {filteredFiles.map((file) => {
                  const Icon = getMimeCategoryIcon(file.mimeCategory, file.mimeType);
                  const canMoveFile = isAgencyMember || file.uploadedBy?.id === user?.id;
                  const isSelected = selectedFileIds.has(file.id);

                  return (
                    <div
                      key={file.id}
                      draggable={canMoveFile}
                      onDragStart={(e) => canMoveFile && handleFileDragStart(e, file.id)}
                      onDragEnd={handleDragEnd}
                      onClick={(e) => {
                        if (!isArchived) {
                          handleFileCheckbox(file.id, e as unknown as MouseEvent);
                        } else {
                          setPreviewFile(file);
                        }
                      }}
                      className={cn(
                        "group relative flex flex-col items-center p-4 rounded-lg border border-grey-100 hover:border-grey-200 hover:bg-grey-50 transition-colors cursor-pointer",
                        canMoveFile && "cursor-grab active:cursor-grabbing",
                        isSelected && "border-accent bg-accent/5"
                      )}
                    >
                      {/* Checkbox */}
                      {!isArchived && (
                        <div className="absolute top-2 left-2">
                          <input
                            type="checkbox"
                            checked={isSelected}
                            onClick={(e) => handleFileCheckbox(file.id, e as unknown as MouseEvent)}
                            onMouseDown={(e) => e.stopPropagation()}
                            onChange={() => {}}
                            className={cn(
                              "w-4 h-4 rounded border-grey-300 text-accent cursor-pointer",
                              hasSelection ? "opacity-100" : "opacity-0 group-hover:opacity-100"
                            )}
                          />
                        </div>
                      )}

                      <div className="w-16 h-16 rounded-lg bg-grey-100 flex items-center justify-center mb-3 overflow-hidden" onClick={(e) => { e.stopPropagation(); setPreviewFile(file); }}>
                        <ImageThumbnail file={file} size="large" />
                      </div>

                      <p className="text-sm font-medium text-secondary text-center truncate w-full cursor-pointer hover:text-accent hover:underline" onClick={(e) => { e.stopPropagation(); setPreviewFile(file); }}>
                        {file.fileName}
                      </p>
                      <p className="text-xs text-grey-500 mt-1 cursor-pointer hover:text-secondary" onClick={(e) => { e.stopPropagation(); setPreviewFile(file); }}>
                        {formatBytes(file.fileSize)}
                      </p>

                      {/* Action buttons on hover */}
                      <div className="absolute top-2 right-2 flex gap-1 opacity-0 group-hover:opacity-100 transition-all">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDownload(file);
                          }}
                          className="p-1.5 bg-white rounded shadow hover:bg-grey-50"
                          title="Download"
                        >
                          <Download className="w-3.5 h-3.5 text-grey-500" />
                        </button>
                        {(isAgencyMember || file.uploadedBy?.id === user?.id) && !isArchived && (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleTrashFile(file);
                            }}
                            className="p-1.5 bg-white rounded shadow hover:bg-error/10"
                            title="Move to Trash"
                          >
                            <Trash2 className="w-3.5 h-3.5 text-grey-500 hover:text-error" />
                          </button>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </Card>
        </div>
      </div>

      {/* File Preview Modal */}
      <FilePreviewModal
        isOpen={!!previewFile}
        onClose={() => setPreviewFile(null)}
        attachment={previewFile}
        attachments={filteredFiles}
        currentIndex={previewFile ? filteredFiles.findIndex(f => f.id === previewFile.id) : 0}
        onNavigate={(index) => setPreviewFile(filteredFiles[index])}
      />

      {/* Create Folder Modal */}
      {projectId && (
        <CreateFolderModal
          isOpen={showCreateFolderModal}
          projectId={projectId}
          parentId={currentFolderId || undefined}
          onClose={() => setShowCreateFolderModal(false)}
        />
      )}
    </div>
  );
}

// Image thumbnail component that fetches presigned URL
function ImageThumbnail({ file, size = 'small' }: { file: ProjectFile; size?: 'small' | 'large' }) {
  const { data: urlData, refetch } = useQuery({
    queryKey: ['attachment-url', file.id],
    queryFn: () => attachmentsApi.getDownloadUrl(file.id),
    staleTime: 1000 * 60 * 55, // 55 minutes (URLs expire in 1 hour)
  });

  return (
    <div className="w-full h-full relative bg-grey-200 rounded">
      {urlData && (
        <AttachmentThumbnail
          thumbnailUrl={size === 'large' ? urlData.thumbnailMdUrl : urlData.thumbnailSmUrl}
          originalUrl={urlData.downloadUrl}
          mimeType={file.mimeType}
          fileName={file.fileName}
          createdAt={file.createdAt}
          onRefresh={refetch}
          className="w-full h-full rounded overflow-hidden"
          imgClassName="w-full h-full object-cover rounded"
        />
      )}
    </div>
  );
}
